import { computed, inject, Injectable, signal } from '@angular/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { ModalPermissionComponent } from '../pages/meeting-management/components/modal-permission/modal-permission.component';

@Injectable({
  providedIn: 'root'
})
export class LocalMediaPermissionService {
  nzModal = inject(NzModalService);

  private readonly _micPermission = signal<boolean>(false);
  private readonly _camPermission = signal<boolean>(false);
  private readonly _micPermissionState = signal<"granted" | "denied" | "prompt">("prompt");
  private readonly _camPermissionState = signal<"granted" | "denied" | "prompt">("prompt");

  public hasCamDevice = signal<boolean>(false);
  public hasMicDevice = signal<boolean>(false);

  readonly micPermission = this._micPermission.asReadonly();
  readonly camPermission = this._camPermission.asReadonly();
  readonly camPermissionState = this._camPermissionState.asReadonly();
  readonly micPermissionState = this._micPermissionState.asReadonly();

  readonly isMicPermissionGranted = computed(() => {
    return this.micPermission() && this.micPermissionState() !== 'denied';
  });

  readonly isCameraPermissionGranted = computed(() => {
    return this.camPermission() && this.camPermissionState() !== 'denied';
  });

  constructor() {
    navigator.permissions.query({ name: "camera" as PermissionName }).then(status => {
      const currentCamPermission = this.camPermission();
      this._camPermissionState.set(status.state);
      if (currentCamPermission && (status.state === "denied")) {
        this._camPermission.set(false);
        this.checkDevices();
      } else if (!currentCamPermission && (status.state === "granted" || status.state === "prompt")) {
        this._camPermission.set(true);
        this.checkDevices();
      }
      status.onchange = () => {
        const updatedCamPermission = this.camPermission();
        this._camPermissionState.set(status.state);
        if (updatedCamPermission && (status.state === "denied")) {
          this._camPermission.set(false);
        } else if (!updatedCamPermission && (status.state === "granted" || status.state === "prompt")) {
          this._camPermission.set(true);
          this.checkDevices();
        }
      }
    });

    navigator.permissions.query({ name: "microphone" as PermissionName }).then(status => {
      const currentMicPermission = this.micPermission();
      this._micPermissionState.set(status.state);
      if (currentMicPermission && (status.state === "denied")) {
        this._micPermission.set(false);
        this.checkDevices();
      } else if (!currentMicPermission && (status.state === "granted" || status.state === "prompt")) {
        this._micPermission.set(true);
        this.checkDevices();
      }
      status.onchange = () => {
        const updatedMicPermission = this.micPermission();
        this._micPermissionState.set(status.state);
        if (updatedMicPermission && (status.state === "denied")) {
          this._micPermission.set(false);
        } else if (!updatedMicPermission && (status.state === "granted" || status.state === "prompt")) {
          this._micPermission.set(true);
          this.checkDevices();
        }
      }
    });

    navigator.mediaDevices.addEventListener("devicechange", async () => {
      console.log("Thiết bị mic/cam đã thay đổi");
      const devices = await navigator.mediaDevices.enumerateDevices();

      const mics = devices.filter(d => d.kind === 'audioinput');
      const cams = devices.filter(d => d.kind === 'videoinput');

      if (mics.length === 0) {
        this._micPermission.set(false);
      } else {
        this._micPermission.set(true);
      }
      if (cams.length === 0) {
        this._camPermission.set(false);
      } else {
        this._camPermission.set(true);
      }
    });
  }


  setMicPermission(isOn: boolean) {
    this._micPermission.set(isOn);
  }

  setCamPermission(isOn: boolean) {
    this._camPermission.set(isOn);
  }

  async checkDevices() {
    const devices = await navigator.mediaDevices.enumerateDevices();

    const mics = devices.filter(d => d.kind === 'audioinput');
    const cams = devices.filter(d => d.kind === 'videoinput');

    if (mics.length === 0) {
      this._micPermission.set(false);
      this.hasMicDevice.set(false);
    } else {
      this.hasMicDevice.set(true);
    }
    if (cams.length === 0) {
      this._camPermission.set(false);
      this.hasCamDevice.set(false);
    } else {
      this.hasCamDevice.set(true);
    }
  }


  requestCameraMicPermission = async ():Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      
      stream.getTracks().forEach(track => track.stop());
      return true;

    } catch (error) {
      console.error("Bị từ chối hoặc lỗi:", error);
      return false;
    }
  };


  handleCheckDevicePermissionCall = async (callType: 'audio' | 'video'): Promise<boolean> => {
    if(!this.hasMicDevice() || this.micPermissionState() === 'denied') {
      this.createModalPermission('mic');
      return false;
    }

    if(!this.hasCamDevice() || this.camPermissionState() === 'denied') {
      this.createModalPermission('cam');
      return false;
    }

    if(this.camPermissionState() !== 'granted' || this.micPermissionState() !== 'granted') {
      const requestResult = await this.requestCameraMicPermission();
      return requestResult;
    }

    return true;
  }

   createModalPermission(type: "mic" | "cam") {
      const modalRef = this.nzModal.create<ModalPermissionComponent>({
        nzContent: ModalPermissionComponent,
        nzClosable: true,
        nzMaskClosable: false,
        nzCentered: true,
        nzWidth: '480px',
        nzFooter: null,
        nzData: {
          type,
          into: "call"
        },
      });
    }
}
